import { type ReactNode } from 'react';
import FocusLock from 'react-focus-lock';
import { AnimatePresence, motion } from 'framer-motion';

type ModalProps = {
  open: boolean;
  onClose?: () => void;
  /** When false, clicking the scrim does nothing (critical modals). */
  dismissOnScrim?: boolean;
  /** Renders a coral circular ✕ in the top-right corner that calls onClose. */
  showCloseButton?: boolean;
  closeLabel?: string;
  role?: 'dialog' | 'alertdialog';
  labelledBy?: string;
  /** Card width — 'md' (default) for single-column content, 'xl' for wider grids. */
  size?: 'md' | 'xl';
  children: ReactNode;
};

const SIZE_CLASSES = { md: 'max-w-md', xl: 'max-w-xl' } as const;

/**
 * Base modal primitive: scrim, focus trap, escape/scrim dismissal.
 * Critical modals (idle, print error) pass dismissOnScrim={false}.
 */
export function Modal({
  open,
  onClose,
  dismissOnScrim = true,
  showCloseButton = false,
  closeLabel = 'Tutup',
  role = 'dialog',
  labelledBy,
  size = 'md',
  children,
}: ModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-espresso/50 fixed inset-0 z-50 flex items-center justify-center px-8"
          onClick={() => dismissOnScrim && onClose?.()}
        >
          <FocusLock returnFocus>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              role={role}
              aria-modal="true"
              aria-labelledby={labelledBy}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full ${SIZE_CLASSES[size]} rounded-card bg-cream p-8 shadow-2xl`}
            >
              {showCloseButton && (
                <button
                  type="button"
                  aria-label={closeLabel}
                  onClick={() => onClose?.()}
                  className="absolute right-5 top-5 flex size-9 items-center justify-center rounded-full border-2 border-sakura text-lg font-bold text-sakura-deep"
                >
                  ✕
                </button>
              )}
              {children}
            </motion.div>
          </FocusLock>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
